// REACT
import { useEffect, useState } from "react";

// MATERIAL UI
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
  linearProgressClasses,
} from "@mui/material";

// COMPONENTS
import ContainedButton from "src/components/common/ContainedButton";
import { FormInput, InputStyleType, InputType } from "src/components/input/FormInput";

// REDUX
import api from "src/store/features/api";
import { TaxBitTransaction } from "src/store/features/api/generated";
import {
  DraftLot,
  clearDraftLots,
  setDraftLots,
  useDraftLotsForTransaction,
} from "src/store/features/taxCenterDrafts.slice";
import { useAppDispatch } from "src/store/store";

// STYLES & UTILS
import {
  AMBER,
  BORDER,
  Eyebrow,
  NAVY,
  SALMON,
  TEAL,
  TEXT_MUTED,
  TEXT_SECONDARY,
} from "./styles";
import { formatUnits, formatUtcDateTime } from "./utils";

const MAX_LOTS = 20;

// A cost basis is a plain USD amount, so anything near this is a typo or a stuck key rather than a
// real figure. The cap also keeps every entered value inside Number's exact-integer range, which is
// what makes the toFixed(2) normalisation below safe.
const MAX_COST_BASIS_USD = 1e15;

// react-number-format options for the cost basis field. The "$" is a NumericFormat prefix rather
// than a MUI start adornment so it sits hard against the digits — an adornment inherits the
// outlined input's 14px root padding plus StyledInputContainer's 12px input padding and ends up
// ~20px from the amount. The formatted display ("$1,234.56") then matches formatUsd() in ./utils,
// which is what the deposits table renders.
const USD_INPUT_PROPS = {
  prefix: "$",
  decimalScale: 2,
  valueIsNumericString: true,
  inputMode: "decimal",
  isAllowed: ({ floatValue }: { floatValue?: number }) =>
    floatValue === undefined || floatValue <= MAX_COST_BASIS_USD,
};

/**
 * Settle a USD amount to whole cents, which is all `decimalScale: 2` can ever display.
 *
 * Anything outside Number's exact-integer range is returned untouched: `toFixed` flips to
 * exponential notation past 1e21 ("1e+21") and silently loses integer precision past 2^53, either
 * of which would rewrite the user's figure into a different amount. MAX_COST_BASIS_USD stops those
 * values being entered in the first place, so this is a belt-and-braces guard for values that
 * arrive from TaxBit.
 */
const toWholeCents = (amount: string) => {
  if (amount === "") return "";
  const value = Number(amount);
  return Number.isSafeInteger(Math.trunc(value)) ? value.toFixed(2) : amount;
};

const createEmptyLot = (): DraftLot => ({
  acquisitionDate: "",
  quantity: "",
  costBasisUsd: "",
});

interface ProvideCostBasisModalProps {
  transaction: TaxBitTransaction;
  onClose: () => void;
  onSubmitted: (transactionId: string) => void;
  /**
   * Called when we deleted the transaction's existing lots but the replacement create then
   * failed, so TaxBit is left with zero lots even though it previously had some. The parent
   * should patch its cache back to a "needs review" state — the modal stays open (with the
   * user's entered values intact) rather than closing, since a retry is safe: the DELETE step
   * is idempotent (a re-run 404s and is treated as success) so the next attempt just re-runs
   * the create.
   */
  onReplacementFailed: (transactionId: string) => void;
}

const ProvideCostBasisModal = ({
  transaction,
  onClose,
  onSubmitted,
  onReplacementFailed,
}: ProvideCostBasisModalProps) => {
  const dispatch = useAppDispatch();
  const assetCode = transaction.asset_amount?.asset?.code ?? "";
  const totalUnits = Number(transaction.asset_amount?.amount ?? 0);
  // Date inputs hold "yyyy-mm-dd"; comparing against the deposit's UTC date keeps
  // "acquired on the day of the deposit" valid.
  const depositDateInputValue = (transaction.datetime ?? "").slice(0, 10);

  const draft = useDraftLotsForTransaction(transaction.id);
  const lotsQuery = api.useGetTaxTransferLotsQuery(
    { transactionId: transaction.id },
    { skip: !!draft || !transaction.has_transfer_lot_data },
  );

  const [lots, setLots] = useState<DraftLot[] | null>(() => {
    if (draft?.length) return draft;
    if (transaction.has_transfer_lot_data) return null; // waiting on the lots query
    return [
      {
        acquisitionDate: "",
        quantity: transaction.asset_amount?.amount ?? "",
        costBasisUsd: "",
      },
    ];
  });
  const [lotLoadFailed, setLotLoadFailed] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createTransferLots] = api.useCreateTaxTransferLotsMutation();
  const [deleteTransferLots] = api.useDeleteTaxTransferLotsMutation();

  useEffect(() => {
    if (lots !== null) return;
    if (lotsQuery.data) {
      const existing = lotsQuery.data.data.map((lot) => ({
        acquisitionDate: (lot.acquisition_datetime ?? "").slice(0, 10),
        quantity: lot.quantity ?? "",
        // Settled to cents on the way in: the field can only render 2dp, and completing the review
        // re-posts every lot, so an unrounded value would submit something the user was never shown.
        costBasisUsd: toWholeCents(lot.cost_basis?.amount ?? ""),
      }));
      setLots(existing.length > 0 ? existing : [createEmptyLot()]);
    } else if (lotsQuery.isError) {
      setLotLoadFailed(true);
      setLots([createEmptyLot()]);
    }
  }, [lots, lotsQuery.data, lotsQuery.isError]);

  const updateLot = (index: number, key: keyof DraftLot, value: string) => {
    setLots((prev) =>
      prev ? prev.map((lot, idx) => (idx === index ? { ...lot, [key]: value } : lot)) : prev,
    );
  };

  // Settle the cost basis to whole cents once the user leaves the field, so "1200" reads back as
  // "$1,200.00" rather than "$1,200". Done on blur rather than while typing because padding cents
  // mid-keystroke fights the caret.
  const handleCostBasisBlur = (index: number) => {
    setLots((prev) =>
      prev
        ? prev.map((lot, idx) =>
            idx === index ? { ...lot, costBasisUsd: toWholeCents(lot.costBasisUsd) } : lot,
          )
        : prev,
    );
  };

  const dateError = (lot: DraftLot) =>
    lot.acquisitionDate && depositDateInputValue && lot.acquisitionDate > depositDateInputValue
      ? "Must be on or before the deposit date."
      : "";

  const allocatedUnits = (lots ?? []).reduce((sum, lot) => {
    const quantity = Number(lot.quantity);
    return Number.isFinite(quantity) && quantity > 0 ? sum + quantity : sum;
  }, 0);
  const tolerance = Math.max(1e-8, Math.abs(totalUnits) * 1e-8);
  const fullyAllocated = Math.abs(allocatedUnits - totalUnits) <= tolerance;
  const overAllocated = allocatedUnits - totalUnits > tolerance;

  const lotsValid =
    !!lots &&
    lots.length > 0 &&
    lots.every((lot) => {
      const quantity = Number(lot.quantity);
      const cost = Number(lot.costBasisUsd);
      return (
        lot.acquisitionDate !== "" &&
        !dateError(lot) &&
        lot.quantity !== "" &&
        Number.isFinite(quantity) &&
        quantity > 0 &&
        lot.costBasisUsd !== "" &&
        Number.isFinite(cost) &&
        cost >= 0
      );
    });
  const canComplete = lotsValid && fullyAllocated && !isSubmitting;

  const handleSaveAndExit = () => {
    if (lots) dispatch(setDraftLots({ transactionId: transaction.id, lots }));
    onClose();
  };

  const handleComplete = async () => {
    if (!lots || !canComplete) return;
    setSubmitError("");
    setIsSubmitting(true);
    // Tracks whether this attempt actually deleted TaxBit's existing lots, so the catch block
    // below can tell "nothing changed remotely, safe to just retry" apart from "the old lots
    // are gone and the replacement never landed".
    let deletedExistingLots = false;
    try {
      // We delete before create rather than the other way around: TaxBit's POST semantics on a
      // transaction that already has lots are append-ish (a create-first attempt would leave
      // old + new lots coexisting), and DELETE removes the *entire* lot set for a transaction —
      // there's no per-lot id to target, so a "delete only the old ones" step afterward isn't
      // possible either. Create-then-delete would therefore risk TaxBit briefly double-counting
      // cost basis, then wiping out the new lots along with the old ones.
      //
      // The risk this creates instead — old lots deleted, then the create fails — is handled
      // below rather than avoided: deleteTransferLots is idempotent server-side (a repeat call
      // 404s and is treated as success), so surfacing the failure and letting the user retry is
      // safe and will simply re-run the create.
      if (transaction.has_transfer_lot_data) {
        await deleteTransferLots({ transactionId: transaction.id }).unwrap();
        deletedExistingLots = true;
      }
      await createTransferLots({
        transactionId: transaction.id,
        taxBitCreateTransferLotsDto: {
          lots: lots.map((lot) => ({
            quantity: lot.quantity,
            cost_basis: {
              amount: lot.costBasisUsd,
              asset: { code: "USD" },
            },
            acquisition_datetime: new Date(`${lot.acquisitionDate}T00:00:00.000Z`).toISOString(),
          })),
        },
      }).unwrap();
      dispatch(clearDraftLots(transaction.id));
      onSubmitted(transaction.id);
    } catch (error: unknown) {
      console.warn("Failed to submit transfer lots", error);
      if (deletedExistingLots) {
        // TaxBit now has zero lots for this deposit, but nothing here should let it keep
        // looking "reviewed" — flag it back to needs-review in the parent's cache so the badge
        // and table reflect reality, and tell the user plainly what happened. Their entered
        // values stay on screen (we don't clear `lots`) so retrying is a single click.
        onReplacementFailed(transaction.id);
        setSubmitError(
          "Your previous cost basis was removed but the replacement couldn't be saved. This deposit needs review again — please try submitting once more.",
        );
      } else {
        setSubmitError("Unable to submit your lots right now. Please try again.");
      }
      setIsSubmitting(false);
    }
  };

  const unallocatedUnits = Math.max(0, totalUnits - allocatedUnits);

  return (
    <Dialog
      open
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "10px" } }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{ px: 4, pt: 3.25, pb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Stack spacing={0.75}>
          <Typography sx={{ fontSize: 21, fontWeight: 600, color: NAVY, lineHeight: 1.25 }}>
            Provide cost basis
          </Typography>
          <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>
            Deposit of{" "}
            <Box component="span" sx={{ color: NAVY, fontWeight: 500 }}>
              {formatUnits(transaction.asset_amount?.amount)} {assetCode}
            </Box>{" "}
            received {formatUtcDateTime(transaction.datetime)} UTC ·{" "}
            <Box component="span" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }}>
              {transaction.client_transaction_id ?? transaction.id}
            </Box>
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close cost basis review">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack spacing={2.25} sx={{ px: 4, pt: 2.75, pb: 1, overflow: "auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={2}
          sx={{
            backgroundColor: "#F8F9F9",
            border: `1px solid ${BORDER}`,
            borderRadius: "6px",
            px: 2.25,
            py: 1.75,
          }}>
          <Typography sx={{ fontSize: 14, color: TEXT_SECONDARY }}>Allocated across lots</Typography>
          <Stack direction="row" alignItems="center" gap={1.75} sx={{ flex: 1, maxWidth: 420 }}>
            <LinearProgress
              variant="determinate"
              value={totalUnits > 0 ? Math.min(100, (allocatedUnits / totalUnits) * 100) : 0}
              sx={{
                flex: 1,
                height: 6,
                borderRadius: 3,
                backgroundColor: BORDER,
                [`& .${linearProgressClasses.bar}`]: {
                  backgroundColor: overAllocated ? SALMON : fullyAllocated ? TEAL : AMBER,
                  borderRadius: 3,
                },
              }}
            />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: NAVY, whiteSpace: "nowrap" }}>
              {formatUnits(allocatedUnits)} / {formatUnits(transaction.asset_amount?.amount)}{" "}
              {assetCode}
            </Typography>
          </Stack>
        </Stack>

        {lots === null ? (
          <Skeleton variant="rounded" height={140} />
        ) : (
          <Stack spacing={1.75}>
            {lotLoadFailed && (
              <Typography sx={{ fontSize: 13, color: AMBER }}>
                Unable to load your existing lots — you can still enter them manually.
              </Typography>
            )}
            {lots.map((lot, index) => {
              const lotDateError = dateError(lot);
              return (
                <Stack
                  key={index}
                  spacing={1.75}
                  sx={{ border: `1px solid ${BORDER}`, borderRadius: "6px", p: 2.25, pb: 2.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Eyebrow sx={{ color: TEXT_MUTED }}>{`Lot ${index + 1}`}</Eyebrow>
                    <ButtonBase
                      disabled={lots.length === 1 || isSubmitting}
                      onClick={() => setLots((prev) => prev?.filter((_, idx) => idx !== index) ?? prev)}
                      sx={{
                        fontFamily: "Roobert, sans-serif",
                        fontSize: 13,
                        color: lots.length === 1 ? TEXT_MUTED : SALMON,
                      }}>
                      Remove
                    </ButtonBase>
                  </Stack>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.75}
                    alignItems="flex-start">
                    <FormInput
                      title="Date acquired"
                      type="date"
                      value={lot.acquisitionDate}
                      disabled={isSubmitting}
                      styleType={lotDateError ? InputStyleType.FORM_ERROR : undefined}
                      errorMessage={lotDateError || undefined}
                      onChange={(event) => updateLot(index, "acquisitionDate", event.target.value)}
                    />
                    <FormInput
                      title={`Quantity${assetCode ? ` (${assetCode})` : ""}`}
                      type="number"
                      value={lot.quantity}
                      disabled={isSubmitting}
                      onChange={(event) => updateLot(index, "quantity", event.target.value)}
                    />
                    <FormInput
                      title="Cost basis (USD)"
                      inputType={InputType.NUMBER}
                      value={lot.costBasisUsd}
                      disabled={isSubmitting}
                      placeholder="$0.00"
                      inputProps={USD_INPUT_PROPS}
                      onChange={(event) => updateLot(index, "costBasisUsd", event.target.value)}
                      onBlur={() => handleCostBasisBlur(index)}
                    />
                  </Stack>
                </Stack>
              );
            })}

            {lots.length < MAX_LOTS && (
              <ButtonBase
                onClick={() => setLots((prev) => (prev ? [...prev, createEmptyLot()] : prev))}
                disabled={isSubmitting}
                sx={{
                  justifyContent: "flex-start",
                  gap: 1,
                  px: 2.25,
                  py: 1.625,
                  border: "1px dashed #C9D3D8",
                  borderRadius: "6px",
                  fontFamily: "Roobert, sans-serif",
                  fontSize: 14,
                  fontWeight: 500,
                  color: SALMON,
                }}>
                + Add lot
                <Box component="span" sx={{ color: TEXT_MUTED, fontWeight: 400 }}>
                  · up to {MAX_LOTS} lots per deposit
                </Box>
              </ButtonBase>
            )}
          </Stack>
        )}

        {lots !== null && !fullyAllocated && (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
            sx={{
              backgroundColor: "#FFF9EF",
              border: "1px solid #F3DFBC",
              borderRadius: "6px",
              px: 2,
              py: 1.75,
            }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                flex: "none",
                borderRadius: "50%",
                backgroundColor: AMBER,
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                lineHeight: "16px",
                textAlign: "center",
              }}>
              !
            </Box>
            <Typography sx={{ fontSize: 13, color: "#6B5220", lineHeight: 1.5 }}>
              {overAllocated
                ? `Lot quantities exceed the deposit by ${formatUnits(allocatedUnits - totalUnits)} ${assetCode}. `
                : `${formatUnits(unallocatedUnits)} ${assetCode} is still unallocated. `}
              Lot quantities must add up to the full deposit before you can complete the review —
              or save a draft and finish later.
            </Typography>
          </Stack>
        )}

        {!!submitError && (
          <Typography sx={{ fontSize: 13, color: "error.main" }}>{submitError}</Typography>
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{ px: 4, py: 2.25, mt: 1.5, borderTop: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: 13, color: TEXT_MUTED }}>
          Drafts are kept for this session only, not submitted to the IRS.
        </Typography>
        <Stack direction="row" spacing={1.25}>
          <ContainedButton
            color="neutral"
            width="140px"
            height="44px"
            disabled={isSubmitting}
            onClick={handleSaveAndExit}
            sx={{ background: "#F8F9F9", border: `1px solid ${BORDER}` }}>
            <span style={{ color: NAVY }}>Save &amp; exit</span>
          </ContainedButton>
          <ContainedButton
            color="salmon"
            width="170px"
            height="44px"
            disabled={!canComplete}
            onClick={() => void handleComplete()}>
            {isSubmitting ? "Submitting…" : "Complete review"}
          </ContainedButton>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export default ProvideCostBasisModal;
